import { SagaIterator } from 'redux-saga';
import { persistor } from '../index';
import { all, call, put, take, takeLatest, select } from 'redux-saga/effects';
import {
  CreateProfileAction,
  CREATE_PROFILE,
  SYNC_USER,
  USER_LOGOUT,
  GetUserAction,
  GET_USER,
  EditProfileAction,
  EDIT_PROFILE,
  // GetUserRatingAction,
  GET_USER_RATING,
  Ratings,
} from './types';
import { MCAFirebaseSaga } from '../root-saga';
import { setLoading, setError } from '../app-globals/actions';
import { getUserState } from './selector';
import { getUser, logoutuser, setUserRating } from './actions';
import { setUser } from '../user/actions';
import { getPreferences } from '../settings/actions';
import { getRatings } from '../orders/actions';
import _ from 'lodash';
import { syncNotifications } from '../notifications/actions';
import { firestore } from '../../constants/firebase';
import { FirestoreSnapshot } from '../orders/types';

function* syncUserSaga() {
  const channel = yield call(MCAFirebaseSaga.auth.channel);

  while (true) {
    const { user } = yield take(channel);
    if (user) {
      yield put(getUser(user.uid));
    }
  }
}

function* getUserSaga({ id }: GetUserAction) {
  try {
    const loggedInUser = yield call(
      MCAFirebaseSaga.firestore.getDocument,
      `users/${id}`
    );
    if (loggedInUser.exists) {
      yield put(setUser(loggedInUser.data()));
      yield put(getPreferences());
      yield put(getRatings());
      yield put(syncNotifications());
    } else {
      yield put(
        setError(true, 'There is no account connected with this email')
      );
      yield put(logoutuser());
    }
    yield put(setLoading(false, ''));
  } catch (e) {
    yield put(setLoading(false, ''));
    yield put(setError(true, e.message));
  }
}

function* createProfile({ details }: CreateProfileAction): SagaIterator<void> {
  try {
    const randomGenerator = Math.random()
      .toString(36)
      .substring(2);
    const imageFilename = 'IMG_' + randomGenerator.toUpperCase() + Date.now();
    yield put(setLoading(true, 'Creating profile'));
    const task = yield call(
      MCAFirebaseSaga.storage.uploadString,
      imageFilename,
      details.image,
      'data_url',
      { contentType: 'image/jpeg' }
    );

    yield task; // Wait for upload to complete

    const imageUrl = yield call(
      MCAFirebaseSaga.storage.getDownloadURL,
      imageFilename
    );
    const user = yield select(getUserState);
    const body = {
      username: details.username,
      description: details.description,
      authComplete: true,
      image: imageUrl,
      phone: details.phone,
      address: details.address,
      subAccountType: '',
      location: details.location,
    };
    const updateProfile = yield call(
      MCAFirebaseSaga.firestore.setDocument,
      `users/${user.user.userid}`,
      body,
      { merge: true }
    );
    const preferences = {
      notification: true,
      mealInvisibleMessage: true,
    };
    yield call(
      MCAFirebaseSaga.firestore.setDocument,
      `preferences/${user.user.userid}`,
      preferences,
      { merge: false }
    );
    console.log(updateProfile);
    yield put(getUser(user.user.userid));
  } catch (e) {
    yield put(setLoading(false, ''));
    yield put(setError(true, e.message));
  }
}

function* editProfile({ details }: EditProfileAction) {
  try {
    const { user } = yield select(getUserState);

    // UPLOADS IMAGE FIRST
    if (_.has(details, 'image')) {
      const randomGenerator = Math.random()
        .toString(36)
        .substring(2);
      const imageFilename = 'IMG_' + randomGenerator.toUpperCase() + Date.now();

      const task = yield call(
        MCAFirebaseSaga.storage.uploadString,
        imageFilename,
        details.image || '',
        'data_url',
        { contentType: 'image/jpeg' }
      );

      yield task; // Wait for upload to complete

      const imageUrl = yield call(
        MCAFirebaseSaga.storage.getDownloadURL,
        imageFilename
      );

      details.image = imageUrl;
    }

    // UPDATE DATABASE
    const editResult = yield call(
      MCAFirebaseSaga.firestore.setDocument,
      `users/${user.userid}`,
      details,
      {
        merge: true,
      }
    );

    yield editResult;
    yield put(getUser(user.userid));
    yield put(setLoading(false, 'Profile Updated'));
  } catch (error) {
    yield put(setLoading(false, ''));
    yield put(setError(true, error.message));
  }
}

function* getUserRatings(): SagaIterator<void> {
  try {
    yield put(setLoading(true, 'Getting User Ratings'));
    const firestoreGet = MCAFirebaseSaga.firestore.getCollection;
    const ratingRef = firestore.collection('rating');
    const [userRating] = yield all([call(firestoreGet, ratingRef)]);
    const ratings: Ratings[] = [];
    userRating.forEach((item: FirestoreSnapshot) => {
      const body = {
        id: item.id,
        rate: item.data().rate,
        user: item.data().user,
      };
      ratings.push(body);
    });
    // console.log(ratings);
    yield put(setUserRating(ratings));
    yield put(setLoading(false, ''));
  } catch (error) {
    yield put(setLoading(false, ''));
    console.log(error);
  }
}

function* logout() {
  try {
    setLoading(true, 'Logging out user');
    const data = yield call(MCAFirebaseSaga.auth.signOut);
    persistor.purge();
    console.log(data);
  } catch (e) {
    console.error('Error when logging out', e);
    yield put(setLoading(false, ''));
    yield put(setError(true, e.message));
  }
}

export function* UserSaga(): SagaIterator {
  yield all([
    takeLatest(CREATE_PROFILE, createProfile),
    takeLatest(SYNC_USER, syncUserSaga),
    takeLatest(USER_LOGOUT, logout),
    takeLatest(GET_USER, getUserSaga),
    takeLatest(EDIT_PROFILE, editProfile),
    takeLatest(GET_USER_RATING, getUserRatings),
  ]);
}
